import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Search,
  MapPin,
  Star,
  Phone,
  Globe,
  ExternalLink,
  Filter,
  ChevronDown,
  Truck,
  Shield,
  Wrench,
  Paintbrush,
  Car,
  Warehouse,
  Gauge,
  Cog,
  CheckCircle,
  Plus,
  Navigation,
  Armchair,
  Zap,
  StickyNote,
  X,
  Loader
} from 'lucide-react'
import {
  SERVICE_PROVIDERS,
  SERVICE_CATEGORIES,
  BRAND_SPECIALTIES,
  REGIONS,
  calculateDistance
} from '../../lib/serviceProviders'
import { getServiceProviders } from '../../lib/supabase'
import ProviderReviews from '../../components/ProviderReviews'

// Icon mapping
const CATEGORY_ICONS = {
  all: Cog,
  transport: Truck,
  ppf: Shield,
  mechanical: Wrench,
  bodywork: Paintbrush,
  restoration: Car,
  storage: Warehouse,
  performance: Gauge,
  upholstery: Armchair,
  electrical: Zap
}

// Hudson Valley default coordinates (Hudson, NY)
const HUDSON_NY = { lat: 42.2529, lng: -73.7910 }

const ProviderCard = ({ provider, showDistance }) => (
  <div className={`card p-5 ${provider.featured ? 'ring-1 ring-rdc-orange/20' : ''}`}>
    <div className="flex items-start justify-between mb-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="section-title text-base">{provider.name}</h3>
          {provider.verified && (
            <span className="flex items-center gap-1 text-xs text-rdc-forest bg-rdc-forest/10 px-2 py-0.5 rounded-full">
              <CheckCircle size={10} />
              Verified
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1.5 text-sm text-rdc-taupe">
          <span className="flex items-center gap-1.5">
            <MapPin size={13} />
            {provider.location}
          </span>
          {showDistance && provider.distance !== undefined && (
            <span className="flex items-center gap-1 text-rdc-charcoal font-medium">
              <Navigation size={12} />
              {provider.distance} mi
            </span>
          )}
        </div>
      </div>
      {provider.featured && (
        <span className="text-xs font-semibold text-rdc-orange bg-rdc-orange/8 px-2.5 py-1 rounded-full whitespace-nowrap">
          Featured
        </span>
      )}
    </div>

    <p className="text-sm text-rdc-charcoal mb-4 leading-relaxed">{provider.description}</p>

    {/* Rating & Price */}
    <div className="flex items-center gap-3 mb-4">
      {provider.rating ? (
        <>
          <div className="flex items-center gap-1.5">
            <Star size={14} className="text-amber-500 fill-amber-500" />
            <span className="font-medium text-black text-sm">{provider.rating}</span>
          </div>
          <span className="text-xs text-rdc-taupe">({provider.reviewCount} reviews)</span>
        </>
      ) : (
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(provider.name + ' ' + provider.location)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs text-rdc-taupe hover:text-rdc-orange transition-colors"
        >
          <Star size={12} className="text-rdc-warm-gray" />
          Check Google Reviews
          <ExternalLink size={10} />
        </a>
      )}
      <span className="text-sm font-medium text-rdc-charcoal ml-auto">{provider.priceRange}</span>
    </div>

    {/* Specialties */}
    {provider.specialties.length > 0 && (
      <div className="mb-4">
        <div className="text-label mb-2">Specializes in</div>
        <div className="flex flex-wrap gap-1.5">
          {provider.specialties.slice(0, 4).map(specialty => (
            <span
              key={specialty}
              className="text-xs px-2.5 py-1 bg-rdc-cream text-rdc-charcoal rounded-full font-medium"
            >
              {specialty}
            </span>
          ))}
          {provider.specialties.length > 4 && (
            <span className="text-xs px-2 py-1 text-rdc-taupe">
              +{provider.specialties.length - 4} more
            </span>
          )}
        </div>
      </div>
    )}

    {/* Services */}
    <div className="mb-4">
      <div className="text-label mb-2">Services</div>
      <div className="flex flex-wrap gap-1.5">
        {provider.services.slice(0, 3).map(service => (
          <span
            key={service}
            className="text-xs px-2.5 py-1 bg-rdc-orange/8 text-rdc-orange rounded-full font-medium"
          >
            {service}
          </span>
        ))}
        {provider.services.length > 3 && (
          <span className="text-xs px-2 py-1 text-rdc-taupe">
            +{provider.services.length - 3} more
          </span>
        )}
      </div>
    </div>

    {/* Notes */}
    {provider.notes && (
      <div className="mb-4 p-3 bg-rdc-cream/50 rounded-lg flex items-start gap-2">
        <StickyNote size={14} className="text-rdc-taupe flex-shrink-0 mt-0.5" />
        <p className="text-xs text-rdc-charcoal italic">{provider.notes}</p>
      </div>
    )}

    {/* Actions */}
    <div className="flex items-center gap-4 pt-4 border-t border-rdc-stone/30">
      {provider.phone && (
        <a
          href={`tel:${provider.phone}`}
          className="flex items-center gap-2 text-sm text-rdc-charcoal hover:text-rdc-orange transition-colors"
        >
          <Phone size={14} />
          {provider.phone}
        </a>
      )}
      <div className="flex items-center gap-3 ml-auto">
        {(!provider.claim_status || provider.claim_status === 'unclaimed') && (
          <Link
            to={`/specialists/claim/${provider.id}`}
            className="text-xs text-rdc-taupe hover:text-rdc-orange transition-colors underline"
          >
            Claim this listing
          </Link>
        )}
        {provider.website && (
          <a
            href={provider.website}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-sm text-rdc-orange hover:text-rdc-orange/80 font-medium"
          >
            <Globe size={14} />
            Website
            <ExternalLink size={11} />
          </a>
        )}
      </div>
    </div>

    {/* Member Reviews */}
    <ProviderReviews provider={provider} />
  </div>
)

export default function Specialists() {
  const [providers, setProviders] = useState([])
  const [dbLoading, setDbLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedBrand, setSelectedBrand] = useState('')
  const [selectedRegion, setSelectedRegion] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [useLocation, setUseLocation] = useState(false)
  const [userLocation, setUserLocation] = useState(null)
  const [searchRadius, setSearchRadius] = useState(50)
  const [locationError, setLocationError] = useState('')

  // Fetch providers from DB, fallback to static data
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const { data, error } = await getServiceProviders()
        if (error || !data || data.length === 0) {
          // Fallback to static data
          setProviders(SERVICE_PROVIDERS)
        } else {
          // Map DB fields to match the component's expected shape
          setProviders(data.map(p => ({
            ...p,
            reviewCount: p.review_count || 0,
            priceRange: p.price_range || '$$',
            specialties: p.specialties || [],
            services: p.services || [],
            coordinates: p.coordinates || null
          })))
        }
      } catch {
        setProviders(SERVICE_PROVIDERS)
      } finally {
        setDbLoading(false)
      }
    }
    fetchProviders()
  }, [])

  // Get user's location
  const getUserLocation = () => {
    setLocationError('')
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser')
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        })
        setUseLocation(true)
      },
      (error) => {
        setLocationError('Unable to retrieve your location. Using Hudson Valley as default.')
        setUserLocation(HUDSON_NY)
        setUseLocation(true)
      }
    )
  }

  // Filter providers
  const getFilteredProviders = () => {
    let filtered = [...providers]

    // If using location search, calculate distances and filter
    if (useLocation && userLocation) {
      filtered = filtered
        .map(p => {
          if (!p.coordinates) return null
          const dist = calculateDistance(userLocation.lat, userLocation.lng, p.coordinates.lat, p.coordinates.lng)
          if (dist > searchRadius) return null
          return { ...p, distance: Math.round(dist) }
        })
        .filter(Boolean)
        .sort((a, b) => a.distance - b.distance)

      if (selectedCategory !== 'all') {
        filtered = filtered.filter(p => p.category === selectedCategory)
      }
      if (selectedBrand) {
        filtered = filtered.filter(p => p.specialties.includes(selectedBrand))
      }
    } else {
      // Regular filtering
      if (selectedCategory !== 'all') {
        filtered = filtered.filter(p => p.category === selectedCategory)
      }
      if (selectedBrand) {
        filtered = filtered.filter(p => p.specialties.includes(selectedBrand))
      }
      if (selectedRegion) {
        filtered = filtered.filter(p => p.region === selectedRegion)
      }
    }

    // Search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(query) ||
        (p.description && p.description.toLowerCase().includes(query)) ||
        p.services.some(s => s.toLowerCase().includes(query)) ||
        p.specialties.some(s => s.toLowerCase().includes(query)) ||
        (p.location && p.location.toLowerCase().includes(query))
      )
    }

    return filtered
  }

  const filteredProviders = getFilteredProviders()
  const featuredProviders = filteredProviders.filter(p => p.featured)
  const otherProviders = filteredProviders.filter(p => !p.featured)

  const clearFilters = () => {
    setSearchQuery('')
    setSelectedCategory('all')
    setSelectedBrand('')
    setSelectedRegion('')
    setUseLocation(false)
    setUserLocation(null)
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <p className="text-label mb-1">Curated Directory</p>
        <h1 className="page-title">Find Specialists</h1>
        <p className="text-body mt-2">
          Vetted service providers for exotic, classic, and collector vehicles in the Northeast
        </p>
      </div>

      {/* List Your Business CTA */}
      <div className="mb-6 p-5 rounded-xl border-2 border-rdc-orange/30 bg-rdc-orange/5 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div>
          <h3 className="font-semibold text-rdc-charcoal text-sm mb-1">Are you a service provider?</h3>
          <p className="text-xs text-rdc-taupe">
            Claim your listing or add your business to connect with collectors
          </p>
        </div>
        <Link to="/specialists/claim" className="btn-primary inline-flex items-center gap-2 whitespace-nowrap text-sm">
          <Plus size={14} />
          List Your Business
        </Link>
      </div>

      {/* Search & Location */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-rdc-taupe" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, service, brand, or location..."
            className="input-field pl-11"
          />
        </div>
        <button
          onClick={getUserLocation}
          className={`btn-secondary flex items-center gap-2 ${useLocation ? 'bg-rdc-orange/10 border-rdc-orange text-rdc-orange' : ''}`}
        >
          <Navigation size={16} />
          {useLocation ? 'Using Location' : 'Near Me'}
        </button>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`btn-secondary flex items-center gap-2 ${showFilters ? 'bg-rdc-cream' : ''}`}
        >
          <Filter size={16} />
          Filters
          <ChevronDown size={14} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Location Error */}
      {locationError && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          {locationError}
        </div>
      )}

      {/* Active Filters */}
      {(useLocation || selectedBrand || selectedRegion || selectedCategory !== 'all') && (
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <span className="text-xs text-rdc-taupe">Active filters:</span>
          {useLocation && (
            <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 bg-rdc-orange/10 text-rdc-orange rounded-full">
              Within {searchRadius} miles
              <button onClick={() => setUseLocation(false)} className="hover:text-rdc-orange/70">
                <X size={12} />
              </button>
            </span>
          )}
          {selectedCategory !== 'all' && (
            <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 bg-rdc-forest/10 text-rdc-forest rounded-full">
              {SERVICE_CATEGORIES.find(c => c.id === selectedCategory)?.label}
              <button onClick={() => setSelectedCategory('all')} className="hover:text-rdc-forest/70">
                <X size={12} />
              </button>
            </span>
          )}
          {selectedBrand && (
            <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 bg-rdc-burgundy/10 text-rdc-burgundy rounded-full">
              {selectedBrand}
              <button onClick={() => setSelectedBrand('')} className="hover:text-rdc-burgundy/70">
                <X size={12} />
              </button>
            </span>
          )}
          {selectedRegion && (
            <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 bg-rdc-olive/10 text-rdc-olive rounded-full">
              {REGIONS.find(r => r.id === selectedRegion)?.label}
              <button onClick={() => setSelectedRegion('')} className="hover:text-rdc-olive/70">
                <X size={12} />
              </button>
            </span>
          )}
          <button
            onClick={clearFilters}
            className="text-xs text-rdc-taupe hover:text-rdc-charcoal underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div className="card p-5 mb-6 animate-fade-in">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Brand Filter */}
            <div>
              <label className="form-label">Brand Specialty</label>
              <select
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                className="input-field"
              >
                <option value="">All Brands</option>
                {BRAND_SPECIALTIES.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>

            {/* Region Filter */}
            <div>
              <label className="form-label">Region</label>
              <select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                className="input-field"
                disabled={useLocation}
              >
                <option value="">All Regions</option>
                {REGIONS.map(region => (
                  <option key={region.id} value={region.id}>{region.label}</option>
                ))}
              </select>
            </div>

            {/* Radius Filter (when using location) */}
            {useLocation && (
              <div>
                <label className="form-label">Search Radius</label>
                <select
                  value={searchRadius}
                  onChange={(e) => setSearchRadius(Number(e.target.value))}
                  className="input-field"
                >
                  <option value={25}>25 miles</option>
                  <option value={50}>50 miles</option>
                  <option value={75}>75 miles</option>
                  <option value={100}>100 miles</option>
                  <option value={150}>150 miles</option>
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Categories */}
      <div className="flex gap-2 overflow-x-auto pb-4 mb-6 -mx-2 px-2 scrollbar-hide">
        {SERVICE_CATEGORIES.map(category => {
          const Icon = CATEGORY_ICONS[category.id] || Cog
          return (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                selectedCategory === category.id
                  ? 'bg-rdc-orange text-white shadow-sm'
                  : 'bg-white text-rdc-charcoal hover:bg-rdc-cream border border-rdc-stone/50'
              }`}
            >
              <Icon size={15} />
              {category.label}
            </button>
          )
        })}
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-rdc-taupe">
          {dbLoading ? 'Loading providers...' : `${filteredProviders.length} provider${filteredProviders.length !== 1 ? 's' : ''} found`}
          {useLocation && userLocation && ` within ${searchRadius} miles`}
        </p>
      </div>

      {/* Loading State */}
      {dbLoading ? (
        <div className="card p-12 text-center">
          <Loader size={32} className="animate-spin text-rdc-orange mx-auto mb-4" />
          <p className="text-body text-rdc-charcoal">Loading service providers...</p>
        </div>
      ) : filteredProviders.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-rdc-cream flex items-center justify-center">
            <Wrench size={28} className="text-rdc-taupe" strokeWidth={1.5} />
          </div>
          <h3 className="section-title mb-2">No providers found</h3>
          <p className="text-body text-rdc-charcoal mb-6">
            Try adjusting your search or filters, or expand your search radius
          </p>
          <button onClick={clearFilters} className="btn-secondary">
            Clear All Filters
          </button>
        </div>
      ) : (
        <>
          {/* Featured Providers */}
          {featuredProviders.length > 0 && (
            <div className="mb-10">
              <h2 className="flex items-center gap-2 text-label mb-4">
                <Star size={14} className="text-rdc-orange" />
                Featured Providers
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {featuredProviders.map(provider => (
                  <ProviderCard
                    key={provider.id}
                    provider={provider}
                    showDistance={useLocation}
                  />
                ))}
              </div>
            </div>
          )}

          {/* All Providers */}
          {otherProviders.length > 0 && (
            <div>
              <h2 className="text-label mb-4">
                {selectedCategory !== 'all'
                  ? SERVICE_CATEGORIES.find(c => c.id === selectedCategory)?.label
                  : 'All Providers'
                }
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {otherProviders.map(provider => (
                  <ProviderCard
                    key={provider.id}
                    provider={provider}
                    showDistance={useLocation}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Regional Focus Note */}
      <div className="mt-8 text-center">
        <p className="text-caption">
          Currently featuring providers in the Northeast US (NY, CT, NJ, MA).
          <br />
          More regions coming soon.
        </p>
      </div>
    </div>
  )
}
